import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, map, Observable, tap, reduce } from 'rxjs';

import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import {
	NotificationFilterEnum,
	NotificationStoreData
} from '@shure/cloud/shared/notifications/shared/models/notification-models';

import { PostNotifications$Params } from '../_AUTOGENERATED_/fn/notification-api-message-center/post-notifications';

import { NotificationsApiService } from './notifications-api.service';

@Injectable({
	providedIn: 'root'
})
export class NotificationsService {
	private readonly notificationsApiService = inject(NotificationsApiService);
	private readonly notificationsStore = inject(NotificationsStore);

	public markAsArchive(
		requestData: PostNotifications$Params,
		updatedNotifications: Array<Pick<NotificationStoreData, 'id'> & Partial<NotificationStoreData>>
	): Observable<unknown> {
		return this.notificationsApiService.postNotifications(requestData).pipe(
			tap(() => {
				this.notificationsStore.prependOrPatchNotifications(updatedNotifications);
			})
		);
	}

	public markAsRead(requestData: PostNotifications$Params): Observable<unknown> {
		return this.notificationsApiService.postNotifications(requestData);
	}

	public markAsTrash(
		requestData: PostNotifications$Params,
		notification: NotificationStoreData
	): Observable<unknown> {
		return this.notificationsApiService.postNotifications(requestData).pipe(
			tap(() => {
				this.notificationsStore.prependOrPatchNotifications([notification]);
			})
		);
	}

	/**
	 * Generates the current UTC timestamp.
	 * A small offset is added to ensure timestamp consistency
	 * during immediate state updates.
	 *
	 * @returns UTC timestamp in ISO 8601 format.
	 */
	public getCurrentDateInUTC(): string {
		const currentDate = new Date(new Date().getTime() + 10000);
		return currentDate.toISOString().slice(0, 19) + 'Z';
	}

	/**
	 * Get all unread notifications.
	 */
	public getAllUnreadNotifications(): Observable<NotificationStoreData[]> {
		return this.notificationsApiService
			.getNotifications({
				filter: NotificationFilterEnum.UNREAD
			})
			.pipe(
				expand((response) => {
					if (!response.next) {
						return EMPTY;
					}

					return this.notificationsApiService.getNotifications({
						filter: NotificationFilterEnum.UNREAD,
						next: response.next
					});
				}),
				map((response) => response.items ?? []),
				reduce((all, items) => [...all, ...(items as NotificationStoreData[])], [] as NotificationStoreData[])
			);
	}
}
